/**
 * liquidation-products.js
 *
 * Fetches products from a Shopify collection, filters to those with >= minDiscount,
 * sorts descending by discount %, and renders product cards into a grid container.
 *
 * Used by sections/liquidation-products.liquid (homepage + collection page).
 * Entry point: window.initLiquidationSection(cfg)
 */

(function () {
  'use strict';

  // -- Pure helpers (exported for console testing) ---------------------------

  /**
   * Returns discount fraction [0-1] or null if data is missing/invalid.
   * @param {number} price - sale price in EUR
   * @param {number} compareAt - original price in EUR
   * @returns {number|null}
   */
  function computeDiscount(price, compareAt) {
    if (!compareAt || compareAt <= 0 || price <= 0 || price >= compareAt) return null;
    return (compareAt - price) / compareAt;
  }

  /**
   * Fetches all products from /collections/{handle}/products.json, page by page.
   * @param {string} collectionHandle
   * @returns {Promise<Array>}
   */
  async function fetchAllProducts(collectionHandle) {
    const all = [];
    let page = 1;
    while (true) {
      let products;
      try {
        const res = await fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        ({ products } = await res.json());
      } catch (e) {
        console.warn('[liquidation] fetch error:', e);
        break;
      }
      if (!products || !products.length) break;
      all.push(...products);
      if (products.length < 250) break;
      page++;
    }
    return all;
  }

  /**
   * Filters products to those with discount >= minDiscount and attaches
   * _price, _compareAt, _discount to each qualifying product object.
   * Uses the first variant's price and compare_at_price.
   * @param {Array} products - raw products from products.json
   * @param {number} minDiscount - e.g. 0.15 for 15%
   * @returns {Array} sorted descending by discount
   */
  function filterAndSort(products, minDiscount) {
    const result = [];
    for (const p of products) {
      const v = p.variants && p.variants[0];
      if (!v) continue;
      // Skip products where no variant is available (out of stock / Изчерпано)
      const anyAvailable = p.variants.some(function (variant) { return variant.available; });
      if (!anyAvailable) continue;
      const price = parseFloat(v.price);
      const compareAt = parseFloat(v.compare_at_price);
      const pct = computeDiscount(price, compareAt);
      if (pct !== null && pct >= minDiscount) {
        result.push(Object.assign({}, p, { _price: price, _compareAt: compareAt, _discount: pct }));
      }
    }
    return result.sort(function (a, b) { return b._discount - a._discount; });
  }

  // -- Formatting ------------------------------------------------------------

  var CART_ICON = '<svg class="icon icon-cart" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">'
    + '<path d="M2.40039 2.39268C2.06902 2.39268 1.80039 2.6613 1.80039 2.99268C1.80039 3.32405 2.06902 3.59268 2.40039 3.59268V2.39268ZM4.69293 2.99268L5.27206 2.83583C5.20123 2.57429 4.96389 2.39268 4.69293 2.39268V2.99268ZM8.4183 16.7479L7.83916 16.9048C7.91733 17.1933 8.19604 17.3804 8.49272 17.3433L8.4183 16.7479ZM19.881 15.3151L19.9554 15.9104C20.2102 15.8786 20.4165 15.6881 20.4685 15.4366L19.881 15.3151ZM21.6004 7.00462L22.1879 7.12618C22.2245 6.94947 22.1796 6.76569 22.0656 6.62576C21.9517 6.48583 21.7808 6.40462 21.6004 6.40462V7.00462ZM5.77949 7.00462L5.20036 7.16147L5.77949 7.00462ZM2.40039 3.59268H4.69293V2.39268H2.40039V3.59268ZM8.49272 17.3433L19.9554 15.9104L19.8066 14.7197L8.34388 16.1525L8.49272 17.3433ZM20.4685 15.4366L22.1879 7.12618L21.0128 6.88305L19.2934 15.1935L20.4685 15.4366ZM4.11379 3.14953L5.20036 7.16147L6.35863 6.84777L5.27206 2.83583L4.11379 3.14953ZM5.20036 7.16147L7.83916 16.9048L8.99744 16.5911L6.35863 6.84777L5.20036 7.16147ZM21.6004 6.40462H5.77949V7.60462H21.6004V6.40462ZM11.4004 20.4999C11.4004 20.997 10.9974 21.3999 10.5004 21.3999V22.5999C11.6602 22.5999 12.6004 21.6597 12.6004 20.4999H11.4004ZM10.5004 21.3999C10.0033 21.3999 9.60039 20.997 9.60039 20.4999H8.40039C8.40039 21.6597 9.34059 22.5999 10.5004 22.5999V21.3999ZM9.60039 20.4999C9.60039 20.0029 10.0033 19.5999 10.5004 19.5999V18.3999C9.34059 18.3999 8.40039 19.3402 8.40039 20.4999H9.60039ZM10.5004 19.5999C10.9974 19.5999 11.4004 20.0029 11.4004 20.4999H12.6004C12.6004 19.3402 11.6602 18.3999 10.5004 18.3999V19.5999ZM19.4004 20.4999C19.4004 20.997 18.9974 21.3999 18.5004 21.3999V22.5999C19.6602 22.5999 20.6004 21.6597 20.6004 20.4999H19.4004ZM18.5004 21.3999C18.0033 21.3999 17.6004 20.997 17.6004 20.4999H16.4004C16.4004 21.6597 17.3406 22.5999 18.5004 22.5999V21.3999ZM17.6004 20.4999C17.6004 20.0029 18.0033 19.5999 18.5004 19.5999V18.3999C17.3406 18.3999 16.4004 19.3402 16.4004 20.4999H17.6004ZM18.5004 19.5999C18.9974 19.5999 19.4004 20.0029 19.4004 20.4999H20.6004C20.6004 19.3402 19.6602 18.3999 18.5004 18.3999V19.5999Z" fill="currentColor"/>'
    + '</svg>';

  function fmtNum(n) {
    return n.toFixed(2).replace('.', ',');
  }

  function fmtEur(amount) {
    return '\u20AC' + fmtNum(amount);
  }

  function fmtBgn(eurAmount) {
    return fmtNum(eurAmount * 1.95583) + '\u00A0\u043B\u0432.'; // лв.
  }

  function escAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  // -- Card renderer ---------------------------------------------------------

  /**
   * Returns an <li> HTML string for one product card.
   * Produces the same HTML as card-product.liquid with card_style='card'.
   * @param {Object} product - enriched product with _price, _compareAt, _discount
   * @returns {string}
   */
  function renderCard(product) {
    var img = product.images && product.images[0];
    var imgSrc = img ? img.src : '';
    var savings = product._compareAt - product._price;
    var badgeText = '\u0421\u043f\u0435\u0441\u0442\u0438\u00A0' + fmtEur(savings) + '\u00A0/\u00A0' + fmtBgn(savings);
    var title = product.title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    var vendor = product.vendor ? String(product.vendor).replace(/&/g, '&amp;').replace(/</g, '&lt;') : '';
    var href = '/products/' + escAttr(product.handle);
    var bgnSale = fmtNum(product._price * 1.95583) + '\u00A0\u043b\u0432.';
    var bgnCompare = fmtNum(product._compareAt * 1.95583) + '\u00A0\u043b\u0432.';
    var badge = '<span class="badge badge--bottom-left" style="color:#fff;background-color:#10B981;border-color:#10B981">' + badgeText + '</span>';

    return '<li class="grid__item">'
      // card-wrapper
      + '<div class="card-wrapper product-card-wrapper underline-links-hover">'
      + '<div class="card card--card card--product card--media color-scheme-1 gradient" style="--ratio-percent: 100%;">'

      // card__inner — image area
      + '<div class="card__inner ratio" style="--ratio-percent: 100%;">'
      + (imgSrc
          ? '<div class="card__media"><div class="media media--transparent media--hover-effect">'
            + '<img src="' + imgSrc + '" alt="' + title + '" width="560" height="560" loading="lazy" class="motion-reduce">'
            + '</div></div>'
            + '<a href="' + href + '" class="media-link" aria-labelledby="liq-' + product.id + '"> </a>'
            + '<div class="card-quick-add-icon no-js-hidden">'
            + '<a href="' + href + '" class="card-quick-add-icon__btn" aria-label="' + title + '">' + CART_ICON + '</a>'
            + '</div>'
          : '')
      // inner badge (overlaid on image for card style)
      + '<div class="card__content"><div class="card__information">'
      + '<h3 class="card__heading h6 fw-600 body-font-family">'
      + '<a href="' + href + '" class="full-unstyled-link">' + title + '</a>'
      + '</h3></div>'
      + '<div class="card__badge top left">' + badge + '</div>'
      + '</div>'
      + '</div>' // end card__inner

      // outer card__content — title, vendor, price below image
      + '<div class="card__content">'
      + '<div class="card__information">'
      + '<h3 class="card__heading h6 fw-600 body-font-family" id="liq-' + product.id + '">'
      + '<a href="' + href + '" class="full-unstyled-link">' + title + '</a>'
      + '</h3>'
      + '<div class="card-information">'
      + (vendor ? '<div class="caption-with-letter-spacing light">' + vendor + '</div>' : '')
      + '<div class="price price--on-sale"><div class="price__container">'
      + '<div class="price__sale">'
      + '<span class="price-item price-item--sale price-item--eur">' + fmtEur(product._price) + '</span>'
      + '<span class="price-item--last"><s class="price-item price-item--regular price-item--eur">' + fmtEur(product._compareAt) + '</s></span>'
      + '<span class="price-item price-item--bgn">' + bgnSale + '&nbsp;<s>' + bgnCompare + '</s></span>'
      + '</div></div></div>' // end price__sale, price__container, price
      + '</div>' // end card-information
      + '</div>' // end card__information
      + '<div class="card__badge top left">' + badge + '</div>'
      + '</div>' // end outer card__content

      + '</div>' // end card
      + '</div>' // end card-wrapper
      + '</li>';
  }

  // -- Grid renderer (supports load-more) ------------------------------------

  /**
   * Appends a batch of rendered cards to the grid and returns updated offset.
   * @param {HTMLElement} grid
   * @param {Array} products
   * @param {number} offset
   * @param {number} batchSize
   * @returns {number} new offset
   */
  function appendBatch(grid, products, offset, batchSize) {
    var slice = products.slice(offset, offset + batchSize);
    grid.insertAdjacentHTML('beforeend', slice.map(renderCard).join(''));
    return offset + slice.length;
  }

  // -- Entry point -----------------------------------------------------------

  /**
   * Called by each section instance via inline <script>.
   * @param {Object} cfg
   * @param {string} cfg.sectionId  - Shopify section.id
   * @param {string} cfg.collection - collection handle to fetch from
   * @param {number} cfg.minDiscount - e.g. 0.15
   * @param {number} cfg.maxDisplay  - max products to show; 0 = show all with load-more
   */
  window.initLiquidationSection = async function (cfg) {
    var sectionEl = document.getElementById('liquidation-section-' + cfg.sectionId);
    var grid = document.getElementById('liquidation-grid-' + cfg.sectionId);
    if (!sectionEl || !grid) return;

    var products = filterAndSort(await fetchAllProducts(cfg.collection), cfg.minDiscount);

    if (!products.length) {
      sectionEl.style.display = 'none';
      return;
    }

    var BATCH = 24;
    var shown = 0;

    if (cfg.maxDisplay > 0) {
      // Homepage mode: show exactly maxDisplay products, no load-more
      appendBatch(grid, products, 0, cfg.maxDisplay);
    } else {
      // Collection page mode: show first batch, then load-more
      shown = appendBatch(grid, products, 0, BATCH);

      if (shown < products.length) {
        var btn = document.createElement('button');
        btn.className = 'button button--secondary';
        btn.textContent = '\u041f\u043e\u043a\u0430\u0436\u0438 \u043e\u0449\u0435'; // Покажи още
        btn.style.cssText = 'display:block;margin:2rem auto 0';
        sectionEl.querySelector('.collection').appendChild(btn);

        btn.addEventListener('click', function () {
          shown = appendBatch(grid, products, shown, BATCH);
          if (shown >= products.length) btn.remove();
        });
      }
    }
  };

  // Expose for browser console testing
  window._liquidationHelpers = { computeDiscount: computeDiscount, filterAndSort: filterAndSort };
})();
